'use client';

import { AIResponse } from '@/components/features/chat/components/messages/AIResponse';
import { MessageSkeleton } from '@/components/features/chat/components/messages/MessageSkeleton';
import { UserQuestion } from '@/components/features/chat/components/messages/UserQuestion';
import { ErrorBoundary } from '@/components/features/core/error/components/ErrorBoundary';
import { adviceState } from '@/lib/store/advice.store';
import { observer, useComputed } from '@legendapp/state/react';
import { useMemo } from 'react';

/**
 * Helper function to ensure a value is always an array
 * @param value - The value to convert to array
 * @returns The value as an array
 */
const ensureArray = (value: any): any[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

interface MessageContentProps {
  advice: any;
  shouldShowLoading: boolean;
  isLastAdvice?: boolean;
  hasAdviceContent?: boolean;
}

/**
 * Component that renders the content of a message, including user question and AI response
 */
export const MessageContent = observer(
  ({
    advice,
    shouldShowLoading,
    isLastAdvice = false,
    hasAdviceContent = false,
  }: MessageContentProps) => {
    // Get store state
    const storeState = useComputed(() => ({
      status: adviceState.status.get(),
      isStreaming: adviceState.isStreaming.get(),
      streamingId: adviceState.streamingAdviceId.get(),
      isChatLoading: adviceState.isChatLoading.get(),
    }));

    // Extract values to ensure reactivity
    const { status, isStreaming, streamingId, isChatLoading } =
      storeState.get();

    // Simplified message state - rely more on props passed from parent
    const messageState = useMemo(() => {
      // Basic content check - use the parent's hasAdviceContent value first
      const hasContent =
        hasAdviceContent ||
        (typeof advice.advice === 'string' && advice.advice.trim().length > 20);

      // Simple source check
      const hasSourceData = Boolean(
        Array.isArray(advice.sources) && advice.sources.length > 0
      );

      // Get streaming state - both local and global
      const isCurrentlyStreaming = isStreaming && advice.id === streamingId;

      // Just use the shouldShowLoading prop directly
      const showLoading = shouldShowLoading;

      return {
        hasContent,
        hasSourceData,
        isStreaming: isCurrentlyStreaming,
        showLoading,
      };
    }, [advice, hasAdviceContent, shouldShowLoading, isStreaming, streamingId]);

    return (
      <div
        className='flex w-full flex-col space-y-2 sm:space-y-3 md:space-y-4'
        data-message-content-id={advice.id}
        data-showing-loading={messageState.showLoading ? 'true' : 'false'}
        data-has-content={messageState.hasContent ? 'true' : 'false'}
        data-is-streaming={messageState.isStreaming ? 'true' : 'false'}
        data-advice-status={advice.status}
        data-store-status={status}
      >
        {/* Render the user's question */}
        <div
          className='w-full rounded-lg border border-purple-100 bg-white shadow-sm dark:border-opacity-30 dark:bg-gray-900/20 dark:shadow-gray-900/10'
          data-testid='user-message'
        >
          <UserQuestion
            advice={advice}
            title={advice.question || ''}
            isLoading={messageState.showLoading}
          />
        </div>

        {messageState.showLoading &&
          !messageState.isStreaming &&
          isLastAdvice &&
          !messageState.hasContent && (
            <div className='w-full'>
              <MessageSkeleton />
            </div>
          )}

        {/* Render AI response container based on content or state */}
        {(messageState.hasContent ||
          messageState.hasSourceData ||
          messageState.isStreaming) && (
          <div
            className='w-full'
            data-testid={
              messageState.isStreaming
                ? 'streaming-message'
                : 'completed-message'
            }
            data-message-id={advice.id}
            data-store-status={status}
            data-advice-status={advice.status}
            data-is-streaming={messageState.isStreaming ? 'true' : 'false'}
            data-has-content={messageState.hasContent ? 'true' : 'false'}
            data-has-sources={messageState.hasSourceData ? 'true' : 'false'}
            data-is-last={isLastAdvice ? 'true' : 'false'}
            data-should-show-loading={
              messageState.showLoading ? 'true' : 'false'
            }
            data-is-new={advice.isNew ? 'true' : 'false'}
          >
            <ErrorBoundary
              fallback={
                <div className='w-full rounded-lg bg-red-50 px-2 py-2 text-xs text-red-500 dark:bg-red-900/30 dark:text-red-300 sm:px-4 sm:text-sm'>
                  Error rendering response
                </div>
              }
            >
              <AIResponse advice={advice} />
            </ErrorBoundary>
          </div>
        )}
      </div>
    );
  }
);
